import { icons, RadixTabs, RawLink } from '@portal/components'
import React, { useCallback, useState } from 'react'

import { useQueryHistory } from '../../store'
import { buildQueryForEntireCollection } from '../../utils'
import { useDQLEditorContext } from '../dqlEditor/context'

const { ArrowLeft, Book } = icons

/**
 * The top bar for the DQLEditor
 */
export default function TopBar() {
  const {
    appId,
    activeView,
    setActiveView,
    appName,
    appCollections,
    returnToUrl,
    setQuery,
    setActiveRow,
  } = useDQLEditorContext()
  const queries = useQueryHistory(appId || '')

  const [tab, setTab] = useState<'collections' | 'editor'>('collections')

  const onQueryEntireCollection = useCallback(
    (collection: string, limit?: number) => {
      setQuery(buildQueryForEntireCollection(collection, limit))
    },
    [setQuery],
  )

  const handleViewChange = (view: string) => {
    if (view === 'collections' || view === 'editor') {
      // Prevents an empty query from being executed, and therefore
      // being stored in the history.
      if (view === 'collections' && appCollections?.length) {
        onQueryEntireCollection(appCollections[0], 100)
      } else if (view === 'editor') {
        const lastQuery = queries?.find((q) => q.activeView === 'editor')
        setQuery(lastQuery?.query || '', false)
      }

      setTab(view)
      setActiveView(view)
      setActiveRow(undefined)
    }
  }

  return (
    <div className="flex h-12 flex-shrink-0 items-center justify-between border-b border-solid border-border-normal px-4">
      <div className="w-1/3">
        <RawLink
          to={returnToUrl}
          data-testid="returnToAppLink"
          title="Return to Portal"
          className="flex items-center gap-2 font-medium"
        >
          <span
            aria-hidden="true"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-background-surface-secondary hover:bg-background-surface-hovered"
          >
            <ArrowLeft className="h-4 w-4" />
          </span>
          {appName}
        </RawLink>
      </div>

      <div className="flex w-1/3 items-center justify-center">
        <RadixTabs
          defaultValue={activeView}
          value={tab}
          onValueChange={handleViewChange}
          className="flex-shrink-0"
        >
          <RadixTabs.List className="w-full">
            <RadixTabs.Trigger value="collections" className="w-full">
              Collections
            </RadixTabs.Trigger>
            <RadixTabs.Trigger value="editor" className="w-full">
              DQL Editor
            </RadixTabs.Trigger>
          </RadixTabs.List>
        </RadixTabs>
      </div>

      <div className="flex w-1/3 justify-end">
        <RawLink
          href="https://docs.ditto.live/dql"
          isBlank
          className="flex items-center gap-2"
        >
          <Book className="h-4 w-4" aria-hidden="true" />
          Docs
        </RawLink>
      </div>
    </div>
  )
}
